import {
    Alert,
    Autocomplete,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    TextField,
    Typography,
    createFilterOptions,
    RadioGroup,
    Radio,
    FormControlLabel,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "../../../../utils/axiosInstance";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { FormikProps, useFormik } from "formik";
import { useSnackbar } from "notistack";
import React, { Ref, createRef, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { ArrowBack, CloseSharp, StartRounded } from "@mui/icons-material";
import {
    ALLOWED_CONTENT_PROMO,
    CREATE_MAPPING_TITLE,
    DRAFT_STATUS,
    LIVE,
    LIVE_MAPPING_TITLE,
    MIN_SCHEDULE_DURATION,
    PER_DAY_COUNT_HELPER_TEXT,
    PROMO,
    PUBLISH_DATE_HELPER_TEXT,
    TOTAL_COUNT_HELPER_TEXT,
    UNPUBLISH_DATE_HELPER_TEXT,
    UPDATE_MAPPING_TITLE,
} from "../../LandingPage/constants";
import GeneralModalV1 from "../../../UI/atoms/Popup/GeneralModalV1";
import { MILLIS_TO_LOCALE_DATE_TIME_FORMAT_POPUP } from "../../../../utilities/helpers/formatUnix";
import { Router, useLocation, useNavigate } from "react-router";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../../utils/permissions";

const PromoVideoForm = () => {
    const location = useLocation();
    const { hasPermission } = usePermissions();
    const [videos, setVideos] = useState<any>([]);
    const [promoInfo, setPromoInfo] = useState<any>(null);
    const [promoVideos, setPromoVideos] = useState<any>([]);
    const [assets, setAssets] = useState<any>([]);
    const [videoAPILoader, setVideoAPILoader] = useState(false);
    const [saveLoader, setSaveLoader] = useState(false);
    const [scheduleLoader, setScheduleLoader] = useState(false);
    const [unsavedForm, setUnsavedForm] = useState(false);
    const [unsavedFormPopup, setUnsavedFormPopup] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [showPublishConfirmation, setShowPublishConfirmation] =
        useState(false);
    const [showPostPublishConfirmation, setShowPostPublishConfirmation] =
        useState(false);
    const [allowEdit, setAllowEdit] = useState(false);
    const [title, setTitle] = useState("");
    const [publishStatus, setPublishStatus] = useState<any>(null);
    const formikRef = useRef<FormikProps<any>>(null);
    const {
        values,
        errors,
        touched,
        isSubmitting,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        validateForm,
        isValid,
        setValues,
    }: any = useFormik({
        initialValues: {
            promoVideoId: null,
            assetId: null,
            mappingName: null,
            publishDateTime: "",
            unpublishDateTime: "",
            perDayCount: null,
            totalCount: null,
            promoVideoTitle: null,
            assetTitle: null,
            mappingId: null,
            publishArn: null,
            unpublishArn: null,
            controlSettings: "all",
            assetRef: null,
            promoVideoRef: null,
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            mappingName: Yup.string()
                .required("Mapping Name is required")
                .matches(
                    /^[a-zA-Z0-9]{3,30}$/,
                    "Mapping Name must be alphanumeric with 3-30 characters and no spaces"
                ),
            promoVideoId: Yup.string().required("Promo Video ID is required"),
            assetId: Yup.string().required("Asset ID is required"),
            perDayCount: Yup.number()
                .required("Per Day Impressoins display count is required")
                .min(1, "Per Day Count should be greater than 0")
                .max(1000, "Per Day Count should be less than or equal to 1000")
                .test(
                    "lessThanOrEqualToTotalCount",
                    "Per Day Count should be less than or equal to Total Count",
                    function (value) {
                        const totalCount = this.parent.totalCount;
                        if (!totalCount) return true;
                        return totalCount && value <= totalCount;
                    }
                ),
            totalCount: Yup.number()
                .required("Total Impressions display count is required")
                .min(1, "Total Count should be greater than 0")
                .max(1000, "Total Count should be less than or equal to 1000"),
            publishDateTime: Yup.date()
                .nullable()
                .min(new Date(), "Publish Date Time should be a future date"),
            unpublishDateTime: Yup.date()
                .nullable()
                .min(
                    Yup.ref("publishDateTime"),
                    "Unpublish Date Time should be greater than Publish Date Time"
                ),
            assetRef: Yup.string().nullable(),
            promoVideoRef: Yup.string().nullable(),
        }),
        onSubmit: (values) => {
            handleSaveAsDraft(values);
        },
    });
    const lastPathSegment = location.pathname.split("/").pop();

    const [pathName, setPathName] = useState<string>(lastPathSegment || "");

    useEffect(() => {
        getVideosAPI();
        getPromoDetail();
    }, []);

    const onFormChange = (e: any) => {
        setUnsavedForm(true);
    };

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option: any) => option.name + option.id,
    });

    const getPromoDetail = async () => {
        try {
            if (pathName !== "create") {
                const response = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/promo/${pathName}`
                );
                if (response.data.success) {
                    setPromoInfo(response.data?.data);
                    setValues(response?.data?.data);
                    setPublishStatus(response?.data?.data?.publishStatus);
                    if (response?.data?.data?.publishStatus === DRAFT_STATUS) {
                        setTitle(UPDATE_MAPPING_TITLE);
                        changeEditControl(true);
                    } else if (response?.data?.data?.publishStatus === LIVE) {
                        setTitle(LIVE_MAPPING_TITLE);
                    }
                } else {
                    enqueueSnackbar(response.data.message, {
                        variant: "error",
                    });
                }
            } else {
                setTitle(CREATE_MAPPING_TITLE);
                changeEditControl(true);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const changeEditControl = (status: boolean) => {
        if (hasPermission(PERMISSIONS.curation, ACTION.EDIT)) {
            setAllowEdit(status);
        }
    };

    const handleSaveAsDraft = async (values: any) => {
        try {
            setSaveLoader(true);
            const payload = {
                ...values,
                assetId: Number(values.assetId),
                promoVideoId: Number(values.promoVideoId),
                assetRef: values.assetRef || null,
                promoVideoRef: values.promoVideoRef || null,
            };
            // if any no value key is found, remove it
            const filteredPayload = Object.entries(payload).reduce(
                (acc: any, [key, value]) => {
                    if (value) {
                        acc[key] = value;
                    }
                    return acc;
                },
                {}
            );

            const response = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/promo`,
                filteredPayload
            );
            setSaveLoader(false);
            if (response.data?.success) {
                setUnsavedForm(false);
                setPromoInfo(response.data?.data);
                enqueueSnackbar(response.data.message, { variant: "success" });
                formikRef.current?.resetForm();
                navigateBack();
            } else {
                enqueueSnackbar(response.data.message, { variant: "error" });
            }
        } catch (error: any) {
            setSaveLoader(false);
            enqueueSnackbar(error.response?.data?.message, {
                variant: "warning",
            });
        }
    };

    const publishDateTimeCheck = () => {
        const { publishDateTime, unpublishDateTime } = values;
        const currentTime = new Date();
        const publishTime = new Date(publishDateTime);
        const timeDiff = publishTime.getTime() - currentTime.getTime();
        const minsDiff = Math.floor(timeDiff / (1000 * 60));
        if (minsDiff < MIN_SCHEDULE_DURATION) {
            enqueueSnackbar(
                `Publish date time should be greater than ${MIN_SCHEDULE_DURATION} minutes from current time`,
                { variant: "warning" }
            );
            return false;
        } else if (
            unpublishDateTime &&
            publishDateTime &&
            new Date(unpublishDateTime) <= new Date(publishDateTime)
        ) {
            enqueueSnackbar(
                "Unpublish date time should be greater than publish date time",
                { variant: "warning" }
            );
            return false;
        }

        return true;
    };

    const handleSchedule = async (event: any) => {
        try {
            const validPublishDateTime = publishDateTimeCheck();
            if (isValid && validPublishDateTime) {
                setScheduleLoader(true);
                const payload = {
                    ...values,
                    assetId: Number(values.assetId),
                    promoVideoId: Number(values.promoVideoId),
                };
                // if any no value key is found, remove it
                const filteredPayload = Object.entries(payload).reduce(
                    (acc: any, [key, value]) => {
                        if (value) {
                            acc[key] = value;
                        }
                        return acc;
                    },
                    {}
                );
                setShowPublishConfirmation(false);
                const response = await axios.post(
                    `${process.env.BASE_URL}api/v1/cms/promo/schedule`,
                    filteredPayload
                );

                setScheduleLoader(false);
                if (response.data?.success) {
                    setUnsavedForm(false);
                    setPromoInfo(response.data?.data);

                    // enqueueSnackbar(response.data.message, { variant: 'success' })
                    setShowPostPublishConfirmation(true);
                    // formikRef.current?.resetForm()
                } else {
                    enqueueSnackbar(response.data.message, {
                        variant: "error",
                    });
                }
            } else {
                validateForm();
            }
        } catch (error: any) {
            setShowPublishConfirmation(false);
            console.log(error);
            setScheduleLoader(false);
            if (
                error?.response?.data?.error?.code ===
                "400_PROMO_ALREADY_SCHEDULED"
            ) {
                const errorPayload = JSON.parse(
                    error.response?.data?.error?.message
                );
                const publishDateTime = new Date(errorPayload.publishDateTime);
                const hhmm = `${String(publishDateTime.getHours()).padStart(
                    2,
                    "0"
                )}:${String(publishDateTime.getMinutes()).padStart(2, "0")}`;
                enqueueSnackbar(
                    `Auto Publish Date and Time should be later than ${hhmm}`,
                    { variant: "warning" }
                );
            } else {
                error.response?.data?.message &&
                    enqueueSnackbar(error.response?.data?.message, {
                        variant: "warning",
                    });
            }
        }
    };

    const renderOptionsInitial = (options: any) => {
        if (typeof options == "string") {
            const filterVideo = videos.filter(
                (list: any) => list.id === options
            );
            if (filterVideo.length > 0) {
                return filterVideo[0].name + " - " + filterVideo[0].id;
            } else {
                return "";
            }
        } else if (typeof options == "object") {
            return options.name + " - " + options.id;
        }
    };

    const getVideosAPI = async () => {
        try {
            setVideoAPILoader(true);
            const videoInfo = await axios.get(
                `${process.env.BASE_URL}api/v2/cms/videos/all`
            );
            if (videoInfo.data?.success) {
                setVideos(videoInfo.data?.data);
                setPromoVideos(
                    videoInfo.data.data.filter((video: any) =>
                        video.contentType.includes(PROMO)
                    )
                );
                setAssets(
                    videoInfo.data.data.filter((asset: any) =>
                        ALLOWED_CONTENT_PROMO.includes(asset.contentType)
                    )
                );
            } else {
                enqueueSnackbar(videoInfo.data?.message, { variant: "error" });
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        } finally {
            setVideoAPILoader(false);
        }
    };

    const closePromoPublishModal = () => {
        setShowPublishConfirmation(false);
    };

    const handleBackButton = () => {
        if (unsavedForm) {
            setUnsavedFormPopup(true);
        } else {
            history.back();
        }
    };

    const navigateBack = () => history.back();

    return (
        <Grid
            container
            justifyContent={"center"}
            // sx={{ backgroundColor: "#fefefe" }}
        >
            {unsavedFormPopup && (
                <GeneralModalV1
                    confirmMessage="Yes"
                    cancelMessage="Cancel"
                    title="Unsaved Changes"
                    isOpen={unsavedFormPopup}
                    handleClose={() => setUnsavedFormPopup(false)}
                    handleConfirm={navigateBack}
                >
                    <p>
                        You have unsaved data on the page. Do you still want to
                        leave?
                    </p>
                </GeneralModalV1>
            )}
            {showPublishConfirmation && (
                <GeneralModalV1
                    title="Confirm Publish Date"
                    isOpen={showPublishConfirmation}
                    handleClose={closePromoPublishModal}
                    handleConfirm={handleSchedule}
                >
                    <p>
                        Schedule on{" "}
                        {MILLIS_TO_LOCALE_DATE_TIME_FORMAT_POPUP(
                            values?.publishDateTime
                        )}{" "}
                        {dayjs(values.publishDateTime).format("hh:mm A")}?
                    </p>
                </GeneralModalV1>
            )}
            {showPostPublishConfirmation && (
                <GeneralModalV1
                    title="Success"
                    hideCancelButton={true}
                    confirmMessage={"Done"}
                    isOpen={showPostPublishConfirmation}
                    handleClose={() => setShowPostPublishConfirmation(false)}
                    handleConfirm={() => {
                        setShowPostPublishConfirmation(false);
                        navigateBack();
                    }}
                >
                    <p>
                        Mapping is scheduled to go live on{" "}
                        {MILLIS_TO_LOCALE_DATE_TIME_FORMAT_POPUP(
                            values?.publishDateTime
                        )}{" "}
                        {dayjs(values?.publishDateTime).format("hh:mm A")}
                    </p>
                </GeneralModalV1>
            )}

            <Grid
                item
                xs={12}
                md={12}
                lg={8}
                xl={6}
                container
                spacing={2}
                justifyContent={"center"}
            >
                <Grid
                    item
                    container
                    xs={8}
                    md={8}
                    lg={8}
                    xl={8}
                    justifyContent={"space-between"}
                >
                    <Grid item xs={10}>
                        <Typography variant="h6">{title}</Typography>
                    </Grid>
                    <Grid xs={2} item>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={handleBackButton}
                            variant="contained"
                            color="primary"
                        >
                            {" "}
                            Back
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={8}>
                    <TextField
                        label="Mapping Name"
                        value={values.mappingName}
                        onChange={(e) => {
                            onFormChange(e);
                            handleChange(e);
                        }}
                        onBlur={handleBlur}
                        name="mappingName"
                        error={Boolean(
                            touched.mappingName && errors.mappingName
                        )}
                        helperText={touched.mappingName && errors.mappingName}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        fullWidth
                        required
                        disabled={!allowEdit}
                    />
                </Grid>
                <Grid item xs={8}>
                    <Autocomplete
                        id="promoVideoId"
                        onBlur={handleBlur}
                        options={promoVideos}
                        filterOptions={filterOptions}
                        value={String(values.promoVideoId) || ""}
                        includeInputInList
                        onChange={(e, value) => {
                            onFormChange(e);
                            setFieldValue("promoVideoId", value?.id || "");
                            setFieldValue("promoVideoTitle", value?.name || "");
                            setFieldValue(
                                "promoVideoRef",
                                value?.referenceId || null
                            );
                        }}
                        getOptionLabel={(options: any) => {
                            return renderOptionsInitial(options) || "";
                        }}
                        disabled={!allowEdit}
                        renderInput={(params) => (
                            <TextField
                                label="Select Promo Video"
                                variant="outlined"
                                required
                                helperText={
                                    touched.promoVideoId && errors.promoVideoId
                                }
                                error={
                                    touched.promoVideoId &&
                                    Boolean(errors.promoVideoId)
                                }
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {params.InputProps.endAdornment}
                                            {videoAPILoader && (
                                                <CircularProgress size={24} />
                                            )}
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={8}>
                    <Autocomplete
                        id="assetId"
                        onBlur={handleBlur}
                        options={assets}
                        filterOptions={filterOptions}
                        value={String(values.assetId)}
                        includeInputInList
                        onChange={(e, value) => {
                            onFormChange(e);
                            setFieldValue("assetId", value?.id || "");
                            setFieldValue("assetTitle", value?.name || "");
                            setFieldValue(
                                "assetRef",
                                value?.referenceId || null
                            );
                        }}
                        getOptionLabel={(options: any) => {
                            return renderOptionsInitial(options) || "";
                        }}
                        loading={videoAPILoader}
                        disabled={!allowEdit}
                        renderInput={(params) => (
                            <TextField
                                name="assetId"
                                label="Select Assets (Movies/Episodes/Extras)"
                                value={values.assetId}
                                variant="outlined"
                                required
                                helperText={touched.assetId && errors.assetId}
                                error={
                                    touched.assetId && Boolean(errors.assetId)
                                }
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {params.InputProps.endAdornment}
                                            {videoAPILoader && (
                                                <CircularProgress size={24} />
                                            )}
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={8}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Auto Publish Date Time"
                            sx={{ width: "100%" }}
                            format={`MMM DD, YYYY hh:mm A`}
                            value={
                                (values.publishDateTime &&
                                    dayjs(values.publishDateTime)) ||
                                null
                            }
                            onChange={(e: any) => {
                                onFormChange(e);
                                setFieldValue("publishDateTime", e);
                            }}
                            disabled={!allowEdit}
                            closeOnSelect={false}
                            disablePast
                            slotProps={{
                                textField: {
                                    error: Boolean(
                                        touched.publishDateTime &&
                                            errors.publishDateTime
                                    ),
                                    helperText:
                                        (touched.publishDateTime &&
                                            errors.publishDateTime) ||
                                        PUBLISH_DATE_HELPER_TEXT,
                                    onBlur: handleBlur,
                                },
                                actionBar: {
                                    actions: ["clear", "accept"],
                                },
                                field: { clearable: true },
                            }}
                        />

                        {/* <span>{PUBLISH_DATE_HELPER_TEXT}</span> */}
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={8}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Auto Unpublish Date Time"
                            sx={{ width: "100%" }}
                            format={`MMM DD, YYYY hh:mm A`}
                            value={
                                (values.unpublishDateTime &&
                                    dayjs(values.unpublishDateTime)) ||
                                null
                            }
                            onChange={(e: any) => {
                                onFormChange(e);
                                setFieldValue("unpublishDateTime", e);
                            }}
                            disabled={!allowEdit}
                            closeOnSelect={false}
                            disablePast
                            slotProps={{
                                textField: {
                                    error: Boolean(
                                        touched.unpublishDateTime &&
                                            errors.unpublishDateTime
                                    ),
                                    helperText:
                                        (touched.unpublishDateTime &&
                                            errors.unpublishDateTime) ||
                                        UNPUBLISH_DATE_HELPER_TEXT,
                                    onBlur: handleBlur,
                                },
                                actionBar: {
                                    actions: ["clear", "accept"],
                                },
                                field: { clearable: true },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={8}>
                    <Typography variant="h6">
                        Account User related settings
                    </Typography>
                </Grid>
                <Grid item xs={8}>
                    <TextField
                        label="Promo per day impressions display count"
                        value={values.perDayCount}
                        onChange={(e: any) => {
                            if (!isNaN(e.target.value)) {
                                onFormChange(e);
                                handleChange(e);
                            }
                        }}
                        onBlur={handleBlur}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        name="perDayCount"
                        error={Boolean(
                            touched.perDayCount && errors.perDayCount
                        )}
                        helperText={
                            (touched.perDayCount && errors.perDayCount) ||
                            PER_DAY_COUNT_HELPER_TEXT
                        }
                        fullWidth
                        required
                        disabled={!allowEdit}
                    />
                </Grid>
                <Grid item xs={8}>
                    <TextField
                        label="Promo total impressions display count"
                        value={values.totalCount}
                        onChange={(e: any) => {
                            if (!isNaN(e.target.value)) {
                                onFormChange(e);
                                handleChange(e);
                            }
                        }}
                        onBlur={handleBlur}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        name="totalCount"
                        error={Boolean(touched.totalCount && errors.totalCount)}
                        helperText={
                            (touched.totalCount && errors.totalCount) ||
                            TOTAL_COUNT_HELPER_TEXT
                        }
                        fullWidth
                        required
                        disabled={!allowEdit}
                    />
                </Grid>
                <Grid item xs={8}>
                    <Typography variant="h6">
                        Player Controls setting
                    </Typography>
                    <Typography variant="caption">
                        Allowed controls to be displayed on Front-end Bumper
                        Video Player
                    </Typography>
                </Grid>
                <Grid item xs={8}>
                    <RadioGroup
                        name="controlSettings"
                        value={
                            values.controlSettings
                                ? values.controlSettings
                                : "all"
                        }
                        onChange={(e) =>
                            setFieldValue("controlSettings", e.target.value)
                        }
                    >
                        <FormControlLabel
                            value="all"
                            control={<Radio />}
                            label="All controls including skip"
                        />
                        <FormControlLabel
                            value="play-pause"
                            control={<Radio />}
                            label="Play/pause only; no other controls/skip"
                        />
                    </RadioGroup>
                </Grid>

                {allowEdit && (
                    <Grid item xs={8} container justifyContent="flex-end">
                        <Button
                            className="max-content-width float-center ml-1 primary"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={saveLoader}
                            startIcon={
                                saveLoader && <CircularProgress size={24} />
                            }
                            type="button"
                        >
                            {values?.mappingId
                                ? "Update Draft"
                                : "Save as Draft"}
                        </Button>
                        <Button
                            disabled={
                                !(
                                    values.publishDateTime &&
                                    values.unpublishDateTime
                                ) || scheduleLoader
                            }
                            className="max-content-width float-center ml-1 primary"
                            variant="contained"
                            onClick={() => setShowPublishConfirmation(true)}
                            startIcon={
                                scheduleLoader && <CircularProgress size={24} />
                            }
                            type="button"
                        >
                            {"Schedule"}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default PromoVideoForm;
